import * as React from "react";
import { Calendar } from "lucide-react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { DateRangePreset, IDateRange } from "../analytics.types";
import useNotification from "../../../components/notification/useNotification";

interface IDateRangeFilterProps {
  dateRange: IDateRange;
  dateRangePresets: { key: DateRangePreset; label: string }[];
  onPresetChange: (preset: DateRangePreset) => void;
  onApplyCustomDateRange: (startDate: string, endDate: string) => void;
}

type DatePickerSelectedValue =
  | DateObject
  | DateObject[]
  | DateObject[][]
  | null;

const formatDateToIsoString = (dateValue: Date): string => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getFormattedDateFromPickerValue = (
  selectedDateValue: DatePickerSelectedValue,
): string => {
  if (!selectedDateValue) {
    return "";
  }

  if (Array.isArray(selectedDateValue)) {
    const firstSelectedValue = selectedDateValue[0];

    if (!firstSelectedValue) {
      return "";
    }

    if (Array.isArray(firstSelectedValue)) {
      const nestedFirstSelectedValue = firstSelectedValue[0];

      return nestedFirstSelectedValue
        ? nestedFirstSelectedValue.format("YYYY-MM-DD")
        : "";
    }

    return firstSelectedValue.format("YYYY-MM-DD");
  }

  return selectedDateValue.format("YYYY-MM-DD");
};

const DateRangeFilter = ({
  dateRange,
  dateRangePresets,
  onPresetChange,
  onApplyCustomDateRange,
}: IDateRangeFilterProps): React.JSX.Element => {
  const { showWarningNotification } = useNotification();

  const [draftStartDate, setDraftStartDate] = React.useState<string>("");
  const [draftEndDate, setDraftEndDate] = React.useState<string>("");

  React.useEffect(() => {
    if (dateRange.preset !== "custom") {
      return;
    }

    setDraftStartDate(dateRange.startDate || "");
    setDraftEndDate(dateRange.endDate || "");
  }, [dateRange.preset, dateRange.startDate, dateRange.endDate]);

  const getDateObjectFromIsoDate = (
    dateString: string,
  ): DateObject | undefined => {
    if (!dateString) {
      return undefined;
    }

    const [year, month, day] = dateString.split("-").map(Number);

    if (!year || !month || !day) {
      return undefined;
    }

    return new DateObject({
      year,
      month,
      day,
    });
  };

  const todayDateString = React.useMemo(() => {
    return formatDateToIsoString(new Date());
  }, []);

  const todayDateObject = React.useMemo(() => {
    return new DateObject();
  }, []);

  const draftStartDateObject = React.useMemo(() => {
    return getDateObjectFromIsoDate(draftStartDate);
  }, [draftStartDate]);

  const draftEndDateObject = React.useMemo(() => {
    return getDateObjectFromIsoDate(draftEndDate);
  }, [draftEndDate]);

  const hasCustomStartDate = Boolean(draftStartDate);
  const hasCustomEndDate = Boolean(draftEndDate);

  const hasInvalidCustomRange =
    hasCustomStartDate && hasCustomEndDate && draftStartDate > draftEndDate;

  const hasFutureStartDate =
    hasCustomStartDate && draftStartDate > todayDateString;

  const hasFutureEndDate = hasCustomEndDate && draftEndDate > todayDateString;

  const handleStartDateChange = (
    selectedDateValue: DatePickerSelectedValue,
  ): void => {
    const formattedStartDate =
      getFormattedDateFromPickerValue(selectedDateValue);

    setDraftStartDate(formattedStartDate);

    if (
      draftEndDate &&
      formattedStartDate &&
      draftEndDate < formattedStartDate
    ) {
      setDraftEndDate("");
    }
  };

  const handleEndDateChange = (
    selectedDateValue: DatePickerSelectedValue,
  ): void => {
    setDraftEndDate(getFormattedDateFromPickerValue(selectedDateValue));
  };

  const handleApplyClick = (): void => {
    if (!hasCustomStartDate || !hasCustomEndDate) {
      showWarningNotification("Please select both start and end date.");
      return;
    }

    if (hasFutureStartDate || hasFutureEndDate) {
      showWarningNotification("Future dates are not allowed.");
      return;
    }

    if (hasInvalidCustomRange) {
      showWarningNotification("Start date cannot be greater than end date.");
      return;
    }

    onApplyCustomDateRange(draftStartDate, draftEndDate);
  };

  const customDateRangeSummary =
    draftStartDate && draftEndDate
      ? `${draftStartDate} — ${draftEndDate}`
      : "Select custom date range";

  return (
    <div className="date-range-filter">
      <div className="filter-label">
        <Calendar size={16} strokeWidth={1.5} />
        <span>Time Range</span>
      </div>

      <div className="preset-buttons">
        {dateRangePresets.map((presetItem) => (
          <button
            key={presetItem.key}
            type="button"
            className={`preset-btn ${
              dateRange.preset === presetItem.key ? "active" : ""
            }`}
            onClick={() => onPresetChange(presetItem.key)}
          >
            {presetItem.label}
          </button>
        ))}
      </div>

      {dateRange.preset === "custom" && (
        <div className="custom-date-inputs">
          <div className="date-picker-wrapper">
            <DatePicker
              value={draftStartDateObject || null}
              onChange={handleStartDateChange}
              format="YYYY-MM-DD"
              maxDate={todayDateObject}
              inputClass="date-input"
              containerClassName="date-picker-container"
              calendarPosition="bottom-start"
              editable={false}
              portal
              zIndex={9999}
              placeholder="Start date"
            />
          </div>

          <span className="date-separator">to</span>

          <div className="date-picker-wrapper">
            <DatePicker
              value={draftEndDateObject || null}
              onChange={handleEndDateChange}
              format="YYYY-MM-DD"
              minDate={draftStartDateObject}
              maxDate={todayDateObject}
              inputClass="date-input"
              containerClassName="date-picker-container"
              calendarPosition="bottom-start"
              editable={false}
              portal
              zIndex={9999}
              placeholder="End date"
            />
          </div>

          <button
            type="button"
            className="apply-btn"
            disabled={!hasCustomStartDate || !hasCustomEndDate}
            onClick={handleApplyClick}
          >
            Apply
          </button>
        </div>
      )}

      <div className="date-range-summary">
        {dateRange.preset === "custom"
          ? customDateRangeSummary
          : `${dateRange.startDate} — ${dateRange.endDate}`}
      </div>
    </div>
  );
};

export default DateRangeFilter;
